import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SecretInput from '@/components/SecretInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Textarea } from '@/components/ui/textarea';
import {
  Download, Loader2, RefreshCw, Search, FileText, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight,
  CalendarDays, MessageCircle, Mail, Settings, Send, Clock, Database, XCircle, ExternalLink, Share2, Copy, Hammer,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDisplayDate } from '@/utils/dateFormat';
import { GeoFilters } from '@/components/GeoFilters';

const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const dayName = (iso) => {
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' }); }
  catch { return ''; }
};

const whenLabel = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' })
  : '');

// '20:30' -> '8:30 pm'
const clock12 = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm || '';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
};

const REPORTS = [
  { key: 'briefing', label: 'Beat Briefing', note: 'Morning, for the day\'s beat', who: 'FEs', build: 'briefing' },
  { key: 'scorecard', label: 'Beat Scorecard', note: 'Evening, the day\'s result', who: 'FEs', build: 'evening' },
  { key: 'leaders', label: 'Summary for Leaders', note: 'Evening, per reporting manager', who: 'Managers', build: 'evening' },
  { key: 'rollup', label: 'Roll-up', note: 'Evening, for senior leaders', who: 'Senior leaders', build: 'evening' },
  { key: 'monthly', label: 'Monthly Summary', note: 'The month that closed, per manager', who: 'Managers', build: 'monthly', monthly: true },
  { key: 'monthly_rollup', label: 'Monthly Roll-up', note: 'The month that closed, for seniors', who: 'Senior leaders', build: 'monthly', monthly: true },
];
const REPORT = Object.fromEntries(REPORTS.map(r => [r.key, r]));

const STATUS = {
  accepted: { label: 'Queued', cls: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' },
  sent: { label: 'Sent', cls: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' },
  delivered: { label: 'Delivered', cls: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  read: { label: 'Read', cls: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  failed: { label: 'Failed', cls: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' },
  no_phone: { label: 'No number', cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' },
  no_email: { label: 'No email', cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' },
};

function Chip({ status, icon: Icon, fallback }) {
  const cfg = STATUS[status];
  if (!cfg && !fallback) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-heading uppercase px-1.5 py-0.5 rounded ${cfg ? cfg.cls : 'bg-muted text-muted-foreground'}`}>
      {Icon && <Icon size={10} />}{cfg ? cfg.label : fallback}
    </span>
  );
}

function Kpi({ label, value, hint }) {
  return (
    <div className="bg-card border border-border rounded-md p-3">
      <p className="kpi-label text-muted-foreground">{label}</p>
      <p className="text-2xl font-heading font-bold mt-0.5">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

const Caption = ({ children }) => (
  <Label className="block text-xs font-heading uppercase tracking-widest text-muted-foreground">{children}</Label>
);

// The switches as saved: a level with no entry follows whatever covers it.
const marksFromTree = (data) => {
  const marks = { zone: {}, subzone: {}, state: {}, city: {} };
  const walk = (nodes) => (nodes || []).forEach(n => {
    if (n.setting === true || n.setting === false) marks[n.level][n.key] = n.setting;
    walk(n.children);
  });
  walk(data?.territory);
  walk(data?.place);
  return marks;
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
};

const canShareFiles = () => {
  try {
    return !!navigator.canShare && navigator.canShare({ files: [new File([''], 't.pdf', { type: 'application/pdf' })] });
  } catch { return false; }
};

export default function BeatBriefingsPanel() {
  const { api, isSuper, canUpload } = useAuth();
  // Building, sending, testing and checking a report is the Admin level's job - it is what
  // that level exists for, right after an upload. The settings behind those buttons - the Zoko
  // key, the times, the recipient list, which places are switched on - are the Super Admin's.
  const canRun = canUpload;
  const [date, setDate] = useState(todayISO());
  const [report, setReport] = useState('briefing');
  // a monthly report is filed on the first of the month it covers, so whichever day is picked,
  // the page asks for that first
  const monthly = !!REPORT[report]?.monthly;
  const forDate = monthly ? date.slice(0, 8) + '01' : date;
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [place, setPlace] = useState('');      // state
  const [city, setCity] = useState('');
  const [overview, setOverview] = useState(null);
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [watchUntil, setWatchUntil] = useState(0);
  const [zipping, setZipping] = useState(false);
  const [sending, setSending] = useState('');
  const [health, setHealth] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);


  const loadOverview = useCallback(async () => {
    try {
      const r = await api.get(`/reports/overview?date=${forDate}`);
      setOverview(r.data);
      return r.data;
    } catch { setOverview(null); return null; }
  }, [api, forDate]);

  // Every request from this panel carries whichever places are picked.
  const placeParams = (p) => {
    if (zone) p.set('zone', zone);
    if (subzone) p.set('subzone', subzone);
    if (place) p.set('state', place);
    if (city) p.set('city', city);
    return p;
  };
  const placeLabel = [city, place, subzone, zone].filter(Boolean).join(', ');

  const loadRecipients = useCallback(async () => {
    setRecLoading(true);
    try {
      const p = new URLSearchParams({ report, date: forDate });
      placeParams(p);
      const r = await api.get(`/reports/recipients?${p}`);
      setRec(r.data);
    } catch { setRec(null); }
    finally { setRecLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, report, forDate, zone, subzone, place, city]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { setSelected(null); setPdfUrl(null); loadRecipients(); }, [loadRecipients]);
  useEffect(() => {
    api.get(`/briefing/data-health?date=${date}`).then(r => setHealth(r.data)).catch(() => setHealth(null));
  }, [api, date]);
  useEffect(() => () => { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  // A build runs in the background: watch it until it has finished
  const anyBuilding = !!overview && Object.values(overview.reports || {}).some(r => r.building);
  useEffect(() => {
    if (!anyBuilding && Date.now() > watchUntil) return undefined;
    const t = setInterval(async () => {
      const o = await loadOverview();
      const still = o && Object.values(o.reports || {}).some(r => r.building);
      if (!still && Date.now() > watchUntil) { loadRecipients(); setWatchUntil(0); }
    }, 4000);
    return () => clearInterval(t);
  }, [anyBuilding, watchUntil, loadOverview, loadRecipients]);

  const run = overview?.reports?.[report];
  const sched = overview?.schedule;
  const meta = REPORT[report];

  const build = async () => {
    setStarting(true);
    try {
      await api.post('/reports/build', { date: forDate, what: meta.build });
      toast.success(meta.build === 'briefing'
        ? `Building the Beat Briefings for ${formatDisplayDate(date)}…`
        : `Building the scorecards and leaders' summaries for ${formatDisplayDate(date)}…`);
      setWatchUntil(Date.now() + 15000);
      loadOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the build');
    } finally { setStarting(false); }
  };

  const openPdf = async (r) => {
    setSelected(r);
    setPdfLoading(true);
    try {
      const res = await api.get(`/reports/files/${r.id}`, { responseType: 'blob' });
      setPdfUrl(window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'This PDF is no longer kept' : 'Could not load the PDF');
      setPdfUrl(null);
    } finally { setPdfLoading(false); }
  };

  const fetchBlob = async (r) => {
    const res = await api.get(`/reports/files/${r.id}`, { responseType: 'blob' });
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const downloadOne = async (r) => {
    try { saveBlob(await fetchBlob(r), r.file_name); }
    catch { toast.error('Download failed'); }
  };

  const shareOne = async (r) => {
    try {
      const file = new File([await fetchBlob(r)], r.file_name, { type: 'application/pdf' });
      await navigator.share({ files: [file], title: r.file_name, text: `${meta.label} - ${r.name} - ${formatDisplayDate(date)}` });
    } catch (e) { if (e?.name !== 'AbortError') toast.error('Sharing failed'); }
  };

  // Who should have had this on WhatsApp and did not. The CSV carries their mobile numbers so
  // the admin can send by hand; the ZIP puts each person's PDF beside it, which is the rest of
  // that job. Anyone held back by the place switches is not on the list - that was a decision.
  const [missing, setMissing] = useState('');
  const notDelivered = async (as) => {
    setMissing(as);
    try {
      const p = new URLSearchParams({ report, date: forDate, fmt: as });
      placeParams(p);
      const res = await api.get(`/reports/undelivered?${p}`, { responseType: 'blob' });
      const name = (res.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/);
      saveBlob(new Blob([res.data]), name ? name[1] : `not-delivered_${date}.${as}`);
    } catch (e) {
      toast.error(e.response?.status === 404
        ? 'Everyone on this run got their message'
        : 'Could not build the list');
    } finally { setMissing(''); }
  };

  const downloadZip = async () => {
    setZipping(true);
    try {
      const p = new URLSearchParams({ report, date: forDate });
      placeParams(p);
      const res = await api.get(`/reports/zip?${p}`, { responseType: 'blob' });
      const tail = [zone, subzone, place, city].filter(Boolean)
        .map(v => '_' + v.replace(/\s+/g, '')).join('');
      saveBlob(new Blob([res.data]), `keya_pulse_${report}_${date}${tail}.zip`);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Nothing built for this date yet' : 'Download failed');
    } finally { setZipping(false); }
  };

  const send = async (channel, keys) => {
    const tag = `${channel}:${keys ? keys.join(',') : 'all'}`;
    setSending(tag);
    try {
      const r = await api.post('/reports/send', { report, date: forDate, channel, keys });
      if (r.data.started) {
        toast.success('Sending in the background — the statuses below will update');
      } else if (channel === 'email') {
        toast.success(`Email: ${r.data.sent} sent${r.data.no_email ? `, ${r.data.no_email} without an address of their own` : ''}${r.data.failed ? `, ${r.data.failed} failed` : ''}`);
      } else {
        const bits = [`${r.data.sent} sent`];
        if (r.data.failed) bits.push(`${r.data.failed} failed`);
        if (r.data.no_phone) bits.push(`${r.data.no_phone} without a mobile number`);
        (r.data.failed ? toast.error : toast.success)(`WhatsApp: ${bits.join(', ')}${r.data.errors?.length ? ` — ${r.data.errors[0]}` : ''}`);
      }
      setTimeout(() => { loadRecipients(); loadOverview(); }, r.data.started ? 5000 : 400);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(''); }
  };

  const refreshStatus = async () => {
    setSending('refresh');
    try {
      const r = await api.post('/reports/refresh-status', { report, date: forDate });
      toast.success(r.data.reason || `Checked ${r.data.checked} message${r.data.checked === 1 ? '' : 's'}, ${r.data.updated} updated`);
      loadRecipients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reach Zoko'); }
    finally { setSending(''); }
  };

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rec?.recipients || []).filter(r => !q
      || r.name?.toLowerCase().includes(q) || r.key?.toLowerCase().includes(q)
      || r.beats?.some(b => b.toLowerCase().includes(q)));
  }, [rec, search]);

  const counts = useMemo(() => {
    const c = { total: 0, beats: 0, wa: {}, noPhone: 0, placeOff: 0 };
    (rec?.recipients || []).forEach(r => {
      c.total += 1;
      c.beats += r.beats?.length || 0;
      const w = r.deliveries?.whatsapp?.status;
      if (w) c.wa[w] = (c.wa[w] || 0) + 1;
      if (canRun && r.phone === '') c.noPhone += 1;
      if (!r.place_on) c.placeOff += 1;
    });
    return c;
  }, [rec, canRun]);

  // The same rule the server uses for the not-delivered list: a report held back by the place
  // switches does not count as a failure, and delivered or read means it arrived.
  const notArrived = useMemo(() => (rec?.recipients || []).filter(r => {
    if (!r.place_on) return false;
    const st = r.deliveries?.whatsapp?.status;
    return !(st === 'delivered' || st === 'read');
  }).length, [rec]);

  const leaders = report === 'leaders' || report === 'rollup';
  // The month-to-date scorecard is the Beat Scorecard read up to today, so it is offered there
  // and nowhere else. The code behind it still takes a leader; nothing on screen asks for one.
  const showMtd = report === 'scorecard';
  const delivered = (counts.wa.delivered || 0) + (counts.wa.read || 0);
  const inFlight = (counts.wa.accepted || 0) + (counts.wa.sent || 0);
  const nativeShare = canShareFiles();

  // The month so far, built when it is asked for: one FE comes back as a PDF, a leader's whole
  // team as a ZIP with a file each. A team takes a few seconds, so the button says it is working.
  const [mtd, setMtd] = useState('');
  const monthToDate = async (row) => {
    setMtd(row.key);
    try {
      const p = new URLSearchParams({ date: forDate });
      p.set(leaders ? 'leader' : 'fe', row.key);
      const res = await api.get(`/reports/mtd?${p}`, { responseType: 'blob' });
      const name = (res.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/);
      const fallback = `${(row.name || row.key).replace(/\s+/g, '-')}_Month-to-Date_${forDate}` +
        (leaders ? '.zip' : '.pdf');
      saveBlob(new Blob([res.data]), name ? name[1] : fallback);
    } catch (e) {
      toast.error(e.response?.status === 404
        ? 'No beat plan for them, so there is no month to show'
        : 'Could not build the month-to-date scorecard');
    } finally { setMtd(''); }
  };

  // Every FE in the places picked above, a file each. This is the whole month rather than one
  // day, so it covers everyone who works those beats - not only the FEs listed below, who are
  // the ones with a scorecard built for this particular day.
  //
  // A wide run is minutes of drawing, so nobody is sent into it blind: the size is asked for
  // first, and anything long enough to wonder about is put to the person before it starts.
  const [mtdAll, setMtdAll] = useState(false);          // the run itself
  const [mtdAsk, setMtdAsk] = useState(null);           // what a run would cost, while asking
  const [mtdGot, setMtdGot] = useState(0);              // bytes in so far
  const [mtdWant, setMtdWant] = useState(0);            // bytes expected
  const mtdStop = useRef(null);
  const LONG_ENOUGH_TO_ASK = 60;                        // seconds

  const startMonthToDateAll = async () => {
    let scope = null;
    try {
      const p = new URLSearchParams();
      placeParams(p);
      scope = (await api.get(`/reports/mtd/count?${p}`)).data;
    } catch { /* if we cannot say how big it is, just go */ }
    if (!scope || !scope.fes) {
      if (scope) { toast.error('No beat plans in the places picked'); return; }
    } else if (scope.seconds >= LONG_ENOUGH_TO_ASK) {
      setMtdAsk(scope);
      return;
    }
    runMonthToDateAll(scope);
  };

  const runMonthToDateAll = async (scope) => {
    setMtdAsk(null);
    setMtdAll(true);
    setMtdGot(0);
    setMtdWant(scope?.bytes || 0);
    const stop = new AbortController();
    mtdStop.current = stop;
    try {
      const p = new URLSearchParams({ date: forDate });
      placeParams(p);
      const res = await api.get(`/reports/mtd/all?${p}`, {
        responseType: 'blob', signal: stop.signal,
        onDownloadProgress: e => setMtdGot(e.loaded || 0),
      });
      const name = (res.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/);
      saveBlob(new Blob([res.data]), name ? name[1] : `month-to-date_${date}.zip`);
    } catch (e) {
      if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') toast('Stopped');
      else toast.error(e.response?.status === 404
        ? 'No beat plans in the places picked'
        : 'Could not build the month-to-date scorecards');
    } finally {
      mtdStop.current = null;
      setMtdAll(false);
      setMtdGot(0);
      setMtdWant(0);
    }
  };

  const mtdLabel = () => {
    if (!mtdAll) return 'Month to date';
    if (!mtdGot) return 'Starting…';
    const mb = (n) => `${(n / 1e6).toFixed(1)} MB`;
    return mtdWant ? `${mb(mtdGot)} of about ${mb(mtdWant)}` : mb(mtdGot);
  };

  // Only shown when the wait is long enough to be worth warning about; a city takes seconds and
  // is simply downloaded.
  const mtdQuestion = mtdAsk && (
    <Dialog open onOpenChange={o => { if (!o) setMtdAsk(null); }}>
      <DialogContent className="sm:max-w-md" data-testid="mtd-confirm">
        <DialogHeader>
          <DialogTitle className="font-heading">Month to date for {mtdAsk.fes} field executives</DialogTitle>
          <DialogDescription>
            {placeLabel ? `Everyone who works a beat in ${placeLabel}.` : 'Everyone, across every zone.'}
            {' '}One PDF each, {mtdAsk.beats.toLocaleString('en-IN')} beat pages in all.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm">
          This takes about <strong>{mtdAsk.seconds >= 90
            ? `${Math.round(mtdAsk.seconds / 60)} minutes`
            : `${mtdAsk.seconds} seconds`}</strong> to build, and comes to roughly{' '}
          <strong>{(mtdAsk.bytes / 1e6).toFixed(0)} MB</strong>. The download starts straight away
          and fills as the files are drawn; you can stop it at any point.
        </p>
        {!placeLabel && (
          <p className="text-sm text-muted-foreground">
            Narrow it with the Zone, Subzone, State or City filters to make it quicker.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setMtdAsk(null)} data-testid="mtd-confirm-no">Cancel</Button>
          <Button onClick={() => runMonthToDateAll(mtdAsk)} data-testid="mtd-confirm-yes">Download</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const statusLine = () => {
    if (!run) return <span className="text-muted-foreground flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Checking…</span>;
    if (run.building) {
      return <span className="text-sky-700 dark:text-sky-300 flex items-center gap-1.5" data-testid="run-building"><Loader2 size={12} className="animate-spin" /> Building — this takes a minute or two</span>;
    }
    if (run.status === 'none') {
      return (
        <span className="text-muted-foreground" data-testid="run-none">
          Not built yet — {report === 'briefing'
            ? `built automatically the evening before and sent at ${clock12(sched?.briefing_time || '07:30')}`
            : `built automatically at ${clock12(sched?.evening_time || '20:30')} once the day's upload is in`}
          {canRun ? ', or use Build now' : ''}
        </span>
      );
    }
    if (run.status === 'failed') {
      return (
        <span className="text-red-600 dark:text-red-400 flex items-center gap-1.5" data-testid="run-failed">
          <XCircle size={12} /> Last build failed: {run.detail}{run.built_at ? ' — showing the earlier build' : ''}
        </span>
      );
    }
    return (
      <span className="text-green-700 dark:text-green-400 flex items-center gap-1.5 flex-wrap" data-testid="run-ready">
        <CheckCircle2 size={12} /> Built {whenLabel(run.built_at)}{run.built_by ? ` by ${run.built_by}` : ''}
        <span className="text-muted-foreground">&middot; figures up to {run.figures_label}</span>
        {run.stale && <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle size={12} /> new data uploaded since</span>}
      </span>
    );
  };

  return (
    <div className="space-y-5" data-testid="beat-briefings-panel">
      {mtdQuestion}
      <div className="bg-card border border-border rounded-md p-4 space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" role="tablist">
          {REPORTS.map(r => (
            <button key={r.key} role="tab" aria-selected={report === r.key} onClick={() => setReport(r.key)}
              className={`px-3 py-2 rounded-md border text-left transition-colors ${report === r.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border hover:bg-accent'}`}
              data-testid={`report-tab-${r.key}`}>
              <span className="block text-sm font-heading font-semibold leading-tight">{r.label}</span>
              <span className={`block text-[11px] leading-tight mt-0.5 ${report === r.key ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{r.note}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Caption>{report === 'briefing' ? 'Beat day' : 'Day'}</Caption>
            <Input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} className="h-9 w-44 text-sm" data-testid="report-date" />
          </div>
          <div className="space-y-1">
            <Caption>Place</Caption>
            <div className="flex flex-wrap items-center gap-2">
              <GeoFilters api={api} size="md" zone={zone} setZone={setZone}
                subzone={subzone} setSubzone={setSubzone} state={place} setState={setPlace}
                city={city} setCity={setCity} />
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Button variant="outline" onClick={downloadZip} disabled={zipping || !counts.total} className="h-9" data-testid="download-zip-btn">
              {zipping ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />} Download all
            </Button>
            {showMtd && (
              <>
                <Button variant="outline" onClick={startMonthToDateAll} disabled={mtdAll} className="h-9"
                  data-testid="mtd-all-btn"
                  title={placeLabel
                    ? `Every FE in ${placeLabel}, the month so far - one PDF each`
                    : 'Every FE, the month so far - one PDF each. Narrow it with the place filters above to keep it quick.'}>
                  {mtdAll ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CalendarDays size={14} className="mr-2" />}
                  {mtdLabel()}
                </Button>
                {mtdAll && (
                  <Button variant="ghost" className="h-9" data-testid="mtd-stop-btn"
                    onClick={() => mtdStop.current?.abort()}>Stop</Button>
                )}
              </>
            )}
            {canRun && (
              <Button onClick={build} disabled={starting || run?.building} className="h-9" data-testid="build-btn">
                {starting || run?.building ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Hammer size={14} className="mr-2" />}
                {run && run.status !== 'none' ? 'Rebuild' : 'Build now'}
              </Button>
            )}
            {canRun && (
              <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)} className="h-9 w-9"
                title={isSuper ? 'Schedule and WhatsApp' : 'Schedule, and send a test'} data-testid="report-settings-btn">
                <Settings size={16} />
              </Button>
            )}
          </div>
        </div>

        <div className="text-xs flex items-center gap-2 flex-wrap border-t border-border/50 pt-3">
          <CalendarDays size={13} className="text-muted-foreground" />
          <span className="text-muted-foreground">{formatDisplayDate(date)} &middot; {dayName(date)}</span>
          <span className="text-border">|</span>
          {statusLine()}
          <span className="text-border">|</span>
          <span className="text-muted-foreground flex items-center gap-1.5" data-testid="schedule-summary">
            <Clock size={12} />
            {sched?.enabled
              ? `Automatic: briefings ${clock12(sched.briefing_time)}, scorecards and leaders ${clock12(sched.evening_time)}`
              : 'Automatic sending is off'}
          </span>
        </div>
        {report !== 'briefing' && overview?.data && !overview.data.ready && (
          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5" data-testid="upload-pending">
            <AlertTriangle size={12} />
            The day's upload isn't in yet — sales up to {overview.data.sales_to ? formatDisplayDate(overview.data.sales_to) : 'n/a'},
            visits up to {overview.data.visits_to ? formatDisplayDate(overview.data.visits_to) : 'n/a'}.
            The evening run waits for it until {clock12(sched?.evening_until || '21:30')}.
          </p>
        )}
      </div>

      {health?.issues?.length > 0 && (
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-900 rounded-md px-4 py-3" data-testid="data-health-notice">
          <div className="flex items-start gap-2">
            <AlertTriangle size={15} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-heading font-semibold text-red-700 dark:text-red-400">
                Source data is not up to date — reports for {formatDisplayDate(date)} may understate recent activity
              </p>
              <ul className="mt-1 space-y-0.5">
                {health.issues.map((issue, i) => (
                  <li key={i} className="text-xs text-red-700/90 dark:text-red-400/90 before:content-['•'] before:mr-1.5">{issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {health?.sources?.length > 0 && (
        <div className="bg-card border border-border rounded-md" data-testid="data-sources-card">
          <button onClick={() => setHealthOpen(o => !o)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
            {healthOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Database size={14} className="text-muted-foreground" />
            <span className="text-sm font-heading font-semibold">Source data</span>
            <span className="text-xs text-muted-foreground ml-1">
              {health.ok ? 'all up to date' : `${health.stale_count} of ${health.sources.length} need attention`}
            </span>
            <span className="ml-auto flex items-center gap-1">
              {health.sources.map(s => (
                <span key={s.key} title={s.label} className={`w-2 h-2 rounded-full ${
                  s.status === 'ok' ? 'bg-green-500' : s.status === 'stale' ? 'bg-amber-500' : 'bg-red-500'}`} />
              ))}
            </span>
          </button>
          {healthOpen && (
            <div className="border-t border-border/50 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-4 py-2 font-heading font-medium">Dataset</th>
                    <th className="text-left px-3 py-2 font-heading font-medium">Data through</th>
                    <th className="text-left px-3 py-2 font-heading font-medium">Last upload</th>
                    <th className="text-left px-3 py-2 font-heading font-medium">By</th>
                  </tr>
                </thead>
                <tbody>
                  {health.sources.map(s => (
                    <tr key={s.key} className="border-t border-border/20 align-top">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1.5">
                          {s.status === 'ok'
                            ? <CheckCircle2 size={12} className="text-green-600 dark:text-green-400 shrink-0" />
                            : s.status === 'stale'
                              ? <AlertTriangle size={12} className="text-amber-600 dark:text-amber-400 shrink-0" />
                              : <XCircle size={12} className="text-red-600 dark:text-red-400 shrink-0" />}
                          <span className="font-medium">{s.label}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 ml-[18px]">{s.note}</p>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {s.latest_data_date ? formatDisplayDate(s.latest_data_date) : <span className="text-muted-foreground">n/a</span>}
                        {s.days_behind > 0 && (
                          <span className="block text-[11px] text-amber-600 dark:text-amber-400">
                            {s.days_behind} day{s.days_behind === 1 ? '' : 's'} behind
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {s.last_upload
                          ? <>{formatDisplayDate(s.last_upload.uploaded_at.slice(0, 10))}
                              <span className="block text-[11px] text-muted-foreground">
                                {new Date(s.last_upload.uploaded_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                {s.last_upload.records ? ` · ${s.last_upload.records.toLocaleString('en-IN')} rows` : ''}
                              </span></>
                          : <span className="text-muted-foreground">never</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{s.last_upload?.uploaded_by || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {counts.total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label={meta.who} value={counts.total.toLocaleString('en-IN')}
            hint={leaders ? (report === 'leaders' ? `${(run?.summary?.fes || 0).toLocaleString('en-IN')} FEs in their teams` : 'one page each')
              : `${counts.beats.toLocaleString('en-IN')} beats`} />
          <Kpi label="WhatsApp delivered" value={delivered.toLocaleString('en-IN')}
            hint={inFlight ? `${inFlight} more sent, not confirmed yet` : null} />
          <Kpi label="WhatsApp failed" value={(counts.wa.failed || 0).toLocaleString('en-IN')} />
          <Kpi label={canRun ? 'No mobile number' : 'Not on WhatsApp'}
            value={(canRun ? counts.noPhone : counts.placeOff).toLocaleString('en-IN')}
            hint={canRun ? `${counts.placeOff} in places not switched on` : null} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <div className="p-3 border-b border-border/50 space-y-2">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-muted-foreground" />
              <h3 className="font-heading font-semibold text-sm">{meta.label}s</h3>
              <span className="text-xs text-muted-foreground ml-auto">{list.length}</span>
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={leaders ? 'Search name...' : 'Search FE, user id or beat...'} value={search}
                onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="recipient-search" />
            </div>
            {canRun && counts.total > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={!!sending}
                  onClick={() => send('whatsapp')} data-testid="send-all-wa-btn"
                  title="Everyone not yet sent to, in places switched on for WhatsApp">
                  {sending === 'whatsapp:all' ? <Loader2 size={11} className="mr-1 animate-spin" /> : <MessageCircle size={11} className="mr-1" />}
                  Send pending on WhatsApp
                </Button>
                {leaders && (
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={!!sending}
                    onClick={() => send('email')} data-testid="send-all-email-btn">
                    {sending === 'email:all' ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Mail size={11} className="mr-1" />}
                    Email pending
                  </Button>
                )}
                {inFlight > 0 && (
                  <Button variant="ghost" size="sm" className="h-7 text-[11px]" disabled={!!sending} onClick={refreshStatus}
                    title="Ask Zoko for the latest status of messages not confirmed yet">
                    <RefreshCw size={11} className={`mr-1 ${sending === 'refresh' ? 'animate-spin' : ''}`} /> Check statuses
                  </Button>
                )}
                {notArrived > 0 && (
                  <>
                    <Button variant="ghost" size="sm" className="h-7 text-[11px]" disabled={!!missing}
                      onClick={() => notDelivered('csv')} data-testid="not-delivered-csv-btn"
                      title="A list of everyone who did not get it, with their mobile number">
                      {missing === 'csv' ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Download size={11} className="mr-1" />}
                      Not delivered ({notArrived})
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-[11px]" disabled={!!missing}
                      onClick={() => notDelivered('zip')} data-testid="not-delivered-zip-btn"
                      title="The same list with each person's PDF beside it, to send by hand">
                      {missing === 'zip' ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Download size={11} className="mr-1" />}
                      with their PDFs
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="max-h-[32rem] overflow-auto">
            {recLoading ? (
              <p className="p-4 text-xs text-muted-foreground flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading...</p>
            ) : !counts.total ? (
              <p className="p-4 text-xs text-muted-foreground" data-testid="no-recipients">
                Nothing built for {formatDisplayDate(date)}{placeLabel ? ` in ${placeLabel}` : ''} yet.
              </p>
            ) : list.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">No one matches.</p>
            ) : list.map(r => {
              const wa = r.deliveries?.whatsapp;
              const em = r.deliveries?.email;
              return (
                <button key={r.id} onClick={() => openPdf(r)}
                  className={`w-full text-left px-3 py-2 border-b border-border/20 hover:bg-accent/50 transition-colors ${selected?.id === r.id ? 'bg-primary/10' : ''}`}
                  data-testid={`recipient-${r.key}`}>
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-medium truncate flex-1">{r.name}</p>
                    {wa ? <Chip status={wa.status} icon={MessageCircle} />
                      : !r.place_on ? <Chip fallback="Not switched on" icon={MessageCircle} /> : null}
                    {em && <Chip status={em.status === 'sent' ? 'delivered' : em.status} icon={Mail} />}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {leaders ? (r.zones || []).join(', ') : `${r.key} · ${(r.beats || []).join(', ')}`}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-md flex flex-col min-h-[28rem]">
          {!selected ? (
            <div className="p-10 text-center my-auto">
              <FileText size={28} className="mx-auto text-muted-foreground/50" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground mt-3">Select someone to see their {meta.label}</p>
              <p className="text-xs text-muted-foreground/70 mt-1">It is the same PDF they receive</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-border/50 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold truncate">
                    {selected.name} <span className="text-xs font-normal text-muted-foreground ml-1">{leaders ? '' : selected.key}</span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {leaders ? (selected.zones || []).join(', ') : (selected.beats || []).join(', ')}
                    {canRun && selected.phone !== undefined && (
                      <span> &middot; {selected.phone ? `+${selected.phone}` : 'no mobile number'}</span>
                    )}
                    {canRun && leaders && selected.email !== undefined && (
                      <span> &middot; {selected.email || 'no email of their own'}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => downloadOne(selected)} className="h-8 text-xs" data-testid="download-one-btn">
                    <Download size={12} className="mr-1.5" /> PDF
                  </Button>
                  {pdfUrl && (
                    <Button size="sm" variant="outline" onClick={() => window.open(pdfUrl, '_blank', 'noopener')} className="h-8 text-xs">
                      <ExternalLink size={12} className="mr-1.5" /> Open
                    </Button>
                  )}
                  {nativeShare && (
                    <Button size="sm" variant="outline" onClick={() => shareOne(selected)} className="h-8 text-xs">
                      <Share2 size={12} className="mr-1.5" /> Share
                    </Button>
                  )}
                  {showMtd && (
                  <Button size="sm" variant="outline" disabled={!!mtd} onClick={() => monthToDate(selected)}
                    className="h-8 text-xs" data-testid="mtd-btn"
                    title="This FE's month so far, a page per beat on their round">
                    {mtd === selected.key ? <Loader2 size={12} className="mr-1.5 animate-spin" />
                      : <CalendarDays size={12} className="mr-1.5" />}
                    {mtd === selected.key ? 'Building...' : 'Month to date'}
                  </Button>
                  )}
                  {canRun && (
                    <Button size="sm" variant="outline" disabled={!!sending} onClick={() => send('whatsapp', [selected.key])}
                      className="h-8 text-xs" data-testid="send-one-wa-btn">
                      {sending === `whatsapp:${selected.key}` ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <MessageCircle size={12} className="mr-1.5" />}
                      {selected.deliveries?.whatsapp ? 'Send again' : 'Send on WhatsApp'}
                    </Button>
                  )}
                  {canRun && leaders && (
                    <Button size="sm" variant="outline" disabled={!!sending} onClick={() => send('email', [selected.key])}
                      className="h-8 text-xs" data-testid="send-one-email-btn">
                      {sending === `email:${selected.key}` ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Mail size={12} className="mr-1.5" />} Email
                    </Button>
                  )}
                </div>
              </div>
              {Object.entries(selected.deliveries || {}).map(([channel, d]) => (
                <div key={channel} className="px-3 py-2 border-b border-border/40 text-[11px] flex items-start gap-2 flex-wrap">
                  {channel === 'email' ? <Mail size={12} className="mt-0.5" /> : <MessageCircle size={12} className="mt-0.5" />}
                  <Chip status={channel === 'email' && d.status === 'sent' ? 'delivered' : d.status} />
                  <span className="text-muted-foreground">
                    {d.sent_at ? `${whenLabel(d.sent_at)} by ${d.sent_by || '-'}` : ''}
                    {d.attempts > 1 ? ` · ${d.attempts} attempts` : ''}
                    {d.error ? ` · ${d.error}` : ''}
                  </span>
                  {d.history?.length > 1 && (
                    <span className="text-muted-foreground/80 w-full">
                      {d.history.map(h => `${h.status} ${whenLabel(h.at)}`).join(' → ')}
                    </span>
                  )}
                </div>
              ))}
              <div className="flex-1 min-h-[28rem] bg-muted/30">
                {pdfLoading ? (
                  <p className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading the PDF...</p>
                ) : pdfUrl ? (
                  <iframe title={selected.file_name} src={pdfUrl} className="w-full h-full min-h-[28rem] border-0" data-testid="pdf-preview" />
                ) : (
                  <p className="p-6 text-sm text-muted-foreground">No PDF to show.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {rec?.tests?.length > 0 && canRun && (
        <div className="bg-card border border-border rounded-md px-4 py-3 text-xs" data-testid="test-sends">
          <p className="font-heading font-semibold text-sm mb-1.5">Test messages</p>
          {rec.tests.slice().reverse().map((t, i) => (
            <p key={i} className="text-muted-foreground flex items-center gap-2 flex-wrap">
              <Chip status={t.status} /> {t.name} to +{t.address} &middot; {whenLabel(t.sent_at)} by {t.sent_by}{t.error ? ` · ${t.error}` : ''}
            </p>
          ))}
        </div>
      )}

      {canRun && (
        <DeliverySettings open={settingsOpen} onOpenChange={setSettingsOpen} api={api} date={date}
          report={report} onSaved={loadOverview} canEdit={isSuper} />
      )}
    </div>
  );
}

// One row per zone, subzone, state or city. The tree is flattened into a list first so the table
// body stays one plain run of rows. A row with no switch of its own follows the level above it;
// the send itself follows the same rule, with any Off winning over any On.
function placeRowList(nodes, marks, opened, filter, depth, inherited, follows) {
  const out = [];
  (nodes || []).forEach(n => {
    const id = `${n.level}:${n.key}`;
    const mark = marks[n.level]?.[n.key];
    const effective = mark === undefined ? inherited : mark;
    const kids = (n.children || []).filter(c => !filter || c.label.toLowerCase().includes(filter));
    if (filter && !n.label.toLowerCase().includes(filter) && !kids.length) return;
    const isOpen = opened[id] === undefined ? !!filter : opened[id];
    out.push({ n, id, mark, effective, depth, follows, isOpen });
    if (isOpen && kids.length) {
      placeRowList(kids, marks, opened, filter, depth + 1, effective, n.label).forEach(row => out.push(row));
    }
  });
  return out;
}

function PlaceRows({ nodes, marks, setMark, opened, setOpened, filter = '', follows }) {
  const rows = placeRowList(nodes, marks, opened, filter, 0, false, follows);
  return rows.map(row => {
    const n = row.n;
    return (
      <tr key={row.id} className="border-t border-border/40">
        <td className="px-2 py-1.5" style={{ paddingLeft: 8 + row.depth * 18 }}>
          <div className="flex items-center gap-1.5">
            {n.children?.length
              ? <button type="button" className="text-muted-foreground" aria-label={row.isOpen ? 'Collapse' : 'Expand'}
                  onClick={() => setOpened(o => ({ ...o, [row.id]: !row.isOpen }))}>
                  {row.isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </button>
              : <span className="inline-block w-[13px]" />}
            <span className={row.depth ? '' : 'font-medium'}>{n.label}</span>
            {row.mark === undefined && row.follows && <span className="text-[10px] text-muted-foreground">follows {row.follows}</span>}
          </div>
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums">{n.fes}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">
          {n.with_mobile}
          {n.with_mobile < n.fes && <span className="text-amber-600 dark:text-amber-400"> ({n.fes - n.with_mobile} missing)</span>}
        </td>
        <td className="px-2 py-1.5">
          <div className="flex items-center justify-center gap-1.5">
            <Switch checked={!!row.effective} onCheckedChange={v => setMark(n.level, n.key, v)}
              className={row.mark === undefined ? 'opacity-40' : ''}
              data-testid={`place-${n.level}-${n.key}`} />
            {row.mark === undefined
              ? <span className="inline-block w-3" />
              : <button type="button" title="Follow the level above instead" className="text-muted-foreground hover:text-foreground"
                  onClick={() => setMark(n.level, n.key, undefined)}><RotateCcw size={12} /></button>}
          </div>
        </td>
      </tr>
    );
  });
}

function PlaceTable({ title, nodes, marks, setMark, opened, setOpened, filter, follows }) {
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-muted/40">
          <tr>
            <th className="text-left px-2 py-2 font-heading font-medium">{title}</th>
            <th className="text-right px-2 py-2 font-heading font-medium w-16">FEs</th>
            <th className="text-right px-2 py-2 font-heading font-medium w-40">With a mobile number</th>
            <th className="text-center px-2 py-2 font-heading font-medium w-28">WhatsApp</th>
          </tr>
        </thead>
        <tbody>
          <PlaceRows nodes={nodes} marks={marks} setMark={setMark} opened={opened} setOpened={setOpened}
            filter={filter} follows={follows} />
          {!nodes.length && <tr><td className="px-3 py-3 text-muted-foreground" colSpan={4}>Nothing to show yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PlacesTab({ places, marks, setMark, setMarks, opened, setOpened, search, setSearch, sendingNow }) {
  const allOn = () => setMarks({ zone: Object.fromEntries((places?.territory || []).map(z => [z.key, true])),
                                 subzone: {}, state: {}, city: {} });
  const allOff = () => setMarks({ zone: {}, subzone: {}, state: {}, city: {} });
  return (
    <div className="space-y-3" data-testid="places-section">
      <p className="text-xs text-muted-foreground">
        An FE gets their reports on WhatsApp when something covering their beat is switched on here and nothing
        covering it is switched off. A leader’s summary follows the same rule for the beats their team covers,
        on WhatsApp and by email, and the run summary’s ZIP carries only what went out. The reports in the app
        are not affected.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={allOn} data-testid="places-all-on">All on</Button>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={allOff} data-testid="places-all-off">All off</Button>
        <span className="text-[11px] text-muted-foreground ml-auto">
          As saved: {sendingNow} of {places?.summary?.with_mobile ?? 0} FEs on WhatsApp &middot;{' '}
          {places?.summary?.leaders_sending ?? 0} of {places?.summary?.leaders_with_email ?? 0} leaders by email
        </span>
      </div>

      <PlaceTable title="Zone and subzone" nodes={places?.territory || []} marks={marks} setMark={setMark}
        opened={opened} setOpened={setOpened} filter="" />

      <p className="text-xs text-muted-foreground pt-1">
        State and city are exceptions to the zones above: switch a city off inside a zone that is on, or on inside
        one that is off. A city is listed under its own state.
      </p>
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search a state or city..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-8 h-8 text-xs" data-testid="place-search" />
      </div>
      <PlaceTable title="State and city" nodes={places?.place || []} marks={marks} setMark={setMark}
        opened={opened} setOpened={setOpened} filter={search.trim().toLowerCase()} follows="the zone" />

      <p className="text-[11px] text-muted-foreground">
        States and cities come from the outlet master. A beat missing from it follows its zone. Mobile numbers
        and addresses come from the User List upload.
      </p>
    </div>
  );
}

// Whose PDF the test message carries. The whole list, searchable - these run to a few hundred
// names, and the zone the page is filtered to does not narrow it.
function PersonPicker({ people, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const chosen = people.find(p => p.key === value);
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? people.filter(r => `${r.name} ${r.key}`.toLowerCase().includes(needle))
    : people;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" role="combobox" data-testid="wa-test-who"
          className="h-9 w-44 justify-between text-xs font-normal">
          <span className="truncate">{chosen ? chosen.name : 'First on the list'}</span>
          <ChevronDown size={13} className="opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search a name or user id..." className="text-xs"
            value={q} onValueChange={setQ} />
          <CommandList>
            {!people.length && (
              <p className="py-4 text-center text-xs text-muted-foreground">
                {loading ? 'Loading...' : 'Nothing built for this day.'}
              </p>
            )}
            {!!people.length && !shown.length && (
              <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
                Nobody by that name.
              </CommandEmpty>
            )}
            {!!people.length && (
              <CommandGroup>
                {!needle && (
                  <CommandItem value="__first" className="text-xs"
                    onSelect={() => { onChange(''); setOpen(false); }}>First on the list</CommandItem>
                )}
                {shown.map(r => (
                  <CommandItem key={r.key} value={r.key} className="text-xs"
                    onSelect={() => { onChange(r.key); setOpen(false); }}>
                    <span className="truncate">{r.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const WEEK = [['mon', 'M'], ['tue', 'T'], ['wed', 'W'], ['thu', 'T'], ['fri', 'F'], ['sat', 'S'], ['sun', 'S']];

// Which days an upload is expected on - the reminder stays quiet on the rest.
function DayPicker({ value, onChange }) {
  const on = new Set(String(value || '').split(',').map(d => d.trim()).filter(Boolean));
  const toggle = (d) => {
    const next = new Set(on);
    next.has(d) ? next.delete(d) : next.add(d);
    onChange(WEEK.map(([k]) => k).filter(k => next.has(k)).join(','));
  };
  return (
    <div className="flex gap-1">
      {WEEK.map(([key, letter], i) => (
        <button key={key} type="button" onClick={() => toggle(key)} title={key}
          data-testid={`day-${key}`}
          className={`h-7 w-7 rounded text-[11px] border ${on.has(key)
            ? 'bg-primary text-primary-foreground border-primary'
            : 'border-border text-muted-foreground hover:bg-muted'}`}>{letter}</button>
      ))}
    </div>
  );
}

// Who is responsible for getting this file in. People with a login, so the address comes from
// their account and cannot be mistyped.
function OwnerPicker({ users, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const picked = new Set(value || []);
  const needle = q.trim().toLowerCase();
  const shown = needle ? users.filter(u => `${u.name} ${u.email}`.toLowerCase().includes(needle)) : users;
  const names = users.filter(u => picked.has(u.id)).map(u => u.name);
  const toggle = (id) => onChange(picked.has(id) ? (value || []).filter(x => x !== id) : [...(value || []), id]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" role="combobox" data-testid="owner-picker"
          className="h-8 w-full justify-between text-xs font-normal">
          <span className="truncate">{names.length ? names.join(', ') : 'Nobody set'}</span>
          <ChevronDown size={13} className="opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search people..." className="text-xs" value={q} onValueChange={setQ} />
          <CommandList>
            {!shown.length && (
              <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">Nobody by that name.</CommandEmpty>
            )}
            <CommandGroup>
              {shown.map(u => (
                <CommandItem key={u.id} value={u.id} className="text-xs" onSelect={() => toggle(u.id)}>
                  <span className={`mr-2 h-3 w-3 rounded-sm border ${picked.has(u.id) ? 'bg-primary border-primary' : 'border-border'}`} />
                  <span className="truncate">{u.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function LinksTab({ api, data, canEdit, onChange, sched, setS }) {
  // One standing link per leader, emailed to them - never printed in a PDF, since a forwarded
  // report would hand over standing access without the leader realising it. An Admin sees the
  // counts; the address itself and the open log are the Super Admin's.
  const [busy, setBusy] = useState('');
  const [log, setLog] = useState(null);

  if (!data) return <p className="text-sm text-muted-foreground py-6">Loading...</p>;

  const act = async (key, what) => {
    setBusy(key + what);
    try {
      const r = await api.post(`/reports/links/${encodeURIComponent(key)}/${what}`);
      toast[r.data.ok ? 'success' : 'error'](
        r.data.ok ? (what === 'reset' ? 'New link emailed' : 'Link emailed again')
          : (r.data.reason || { no_email: 'No address of their own in the user list',
                                failed: 'The email did not go out' }[r.data.result] || 'Could not send'));
      const fresh = await api.get('/reports/links');
      onChange(fresh.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not do that'); }
    finally { setBusy(''); }
  };

  const showLog = async (key) => {
    setLog({ key, rows: null });
    try {
      const r = await api.get(`/reports/links/${encodeURIComponent(key)}/opens`);
      setLog({ key, rows: r.data });
    } catch { setLog({ key, rows: [] }); }
  };

  const withLink = data.leaders.filter(l => l.has_link).length;
  const noEmail = data.leaders.filter(l => l.allowed && !l.email).length;

  return (
    <div className="space-y-4" data-testid="links-section">
      <div className="rounded-md border border-border p-3">
        <p className="text-sm font-medium">One link per leader, emailed to them</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          It always shows their latest Summary and roll-up with the last week under it, and it is
          never printed in a report - a forwarded PDF would otherwise hand over standing access.
          Leaders in places that are not switched on do not get one.
        </p>
        <p className="text-xs text-muted-foreground mt-1.5">
          {withLink} of {data.leaders.length} leaders have a live link
          {noEmail ? ` · ${noEmail} have no address of their own` : ''}
          {data.base ? '' : ' · set this app\'s public address on the WhatsApp tab first'}
        </p>
      </div>

      {canEdit && (
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Caption>A link lasts (days)</Caption>
            <Input type="number" min={7} max={365} value={sched.leader_link_days ?? 90}
              onChange={e => setS('leader_link_days', parseInt(e.target.value) || 90)}
              className="h-9" data-testid="link-days" />
          </div>
          <div className="space-y-1">
            <Caption>Replace it this early</Caption>
            <Input type="number" min={0} max={30} value={sched.leader_link_renew ?? 7}
              onChange={e => setS('leader_link_renew', parseInt(e.target.value) || 0)}
              className="h-9" />
          </div>
          <div className="space-y-1">
            <Caption>Flag at this many devices</Caption>
            <Input type="number" min={2} max={50} value={sched.leader_link_alert ?? 5}
              onChange={e => setS('leader_link_alert', parseInt(e.target.value) || 5)}
              className="h-9" />
          </div>
        </div>
      )}

      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 font-heading font-medium">Leader</th>
              <th className="text-left px-3 py-2 font-heading font-medium">Link</th>
              <th className="text-center px-3 py-2 font-heading font-medium">Opens</th>
              <th className="text-center px-3 py-2 font-heading font-medium">Devices<br />(30 days)</th>
              <th className="text-right px-3 py-2 font-heading font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {data.leaders.map(l => (
              <tr key={l.key} className="border-t border-border/40">
                <td className="px-3 py-2">
                  <span className="font-medium">{l.name}</span>
                  {!l.allowed && <span className="block text-[10px] text-muted-foreground">place not switched on</span>}
                  {l.allowed && !l.email && <span className="block text-[10px] text-amber-600 dark:text-amber-400">no address of their own</span>}
                </td>
                <td className="px-3 py-2">
                  {!l.has_link ? <span className="text-muted-foreground">none yet</span> : (
                    <>
                      <span className={l.expires_in <= (data.renew || 7) ? 'text-amber-600 dark:text-amber-400' : ''}>
                        {l.expires_in} days left
                      </span>
                      {canEdit && l.url && (
                        <button type="button" onClick={() => { navigator.clipboard?.writeText(l.url); toast.success('Link copied'); }}
                          className="ml-2 underline text-[11px] text-muted-foreground" data-testid="copy-link">copy</button>
                      )}
                      <span className="block text-[10px] text-muted-foreground">
                        {l.emailed_at ? `emailed ${whenLabel(l.emailed_at)}` : 'not emailed yet'}
                      </span>
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {l.opens || 0}
                  {l.last_open && <span className="block text-[10px] text-muted-foreground">{whenLabel(l.last_open)}</span>}
                </td>
                <td className={`px-3 py-2 text-center ${l.spread ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                  {l.devices || 0}
                  {l.spread && <span className="block text-[10px]">looks shared</span>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {canEdit && l.allowed && (
                    <>
                      <Button variant="ghost" size="sm" className="h-7 text-[11px]" disabled={!!busy}
                        onClick={() => act(l.key, 'resend')}>
                        {busy === l.key + 'resend' ? <Loader2 size={11} className="animate-spin" /> : 'Email it'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-[11px] text-red-600 dark:text-red-400"
                        disabled={!!busy} onClick={() => act(l.key, 'reset')}>
                        {busy === l.key + 'reset' ? <Loader2 size={11} className="animate-spin" /> : 'Replace'}
                      </Button>
                      {l.opens > 0 && (
                        <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => showLog(l.key)}>
                          opens
                        </Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {data.leaders.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">
                No leaders&apos; reports have been built yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {log && (
        <div className="rounded-md border border-border p-3" data-testid="link-opens">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-heading font-semibold uppercase tracking-widest text-muted-foreground">
              Recent opens
            </p>
            <button type="button" className="text-[11px] underline text-muted-foreground" onClick={() => setLog(null)}>close</button>
          </div>
          {!log.rows ? <p className="text-xs text-muted-foreground">Loading...</p>
            : log.rows.length === 0 ? <p className="text-xs text-muted-foreground">Never opened.</p> : (
              <div className="max-h-52 overflow-auto">
                {log.rows.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-[11px] py-1 border-b border-border/30 last:border-0">
                    <span className="text-muted-foreground">{whenLabel(r.at)}</span>
                    <span>{r.device}</span>
                    <span className="text-muted-foreground font-mono">{r.ip}</span>
                    <span className="text-muted-foreground">{r.what === 'page' ? 'page' : 'report'}</span>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}


function DeliverySettings({ open, onOpenChange, api, date, report, onSaved, canEdit }) {
  const [tab, setTab] = useState('schedule');
  const [sched, setSched] = useState(null);
  const [wa, setWa] = useState(null);
  const [places, setPlaces] = useState(null);
  const [marks, setMarks] = useState({ zone: {}, subzone: {}, state: {}, city: {} });
  const [opened, setOpened] = useState({});
  const [placeSearch, setPlaceSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [checks, setChecks] = useState(null);
  const [checking, setChecking] = useState(false);
  const [test, setTest] = useState({ report: 'briefing', key: '', phone: '' });
  const [testPeople, setTestPeople] = useState({});   // report|date -> who has a PDF that day
  const [rem, setRem] = useState(null);              // chasing the uploads the reports live on
  const [links, setLinks] = useState(null);          // the standing link each leader pins
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChecks(null);
    api.get('/reports/schedule').then(r => setSched(r.data)).catch(() => setSched(null));
    api.get('/whatsapp/settings').then(r => setWa({ ...r.data, api_key_masked: r.data.api_key, api_key: '' })).catch(() => setWa(null));
    if (canEdit) {
      api.get('/whatsapp/places', { params: { date } }).then(r => { setPlaces(r.data); setMarks(marksFromTree(r.data)); })
        .catch(() => setPlaces({ territory: [], place: [], summary: {} }));
    } else {
      setPlaces({ territory: [], place: [], summary: {} });
    }
    api.get('/reports/reminders').then(r => setRem(r.data)).catch(() => setRem(null));
    api.get('/reports/links').then(r => setLinks(r.data)).catch(() => setLinks(null));
    setTest(t => ({ ...t, report }));
  }, [open, api, report, date, canEdit]);

  // Whoever the test is for: the list for the report picked in Test, not the tab behind it, and
  // never narrowed by the page's zone.
  const testKey = `${test.report}|${date}`;
  useEffect(() => {
    if (!open || testPeople[testKey] !== undefined) return;
    let gone = false;
    api.get('/reports/recipients', { params: { report: test.report, date } })
      .then(r => { if (!gone) setTestPeople(m => ({ ...m, [testKey]: r.data.recipients || [] })); })
      .catch(() => { if (!gone) setTestPeople(m => ({ ...m, [testKey]: [] })); });
    return () => { gone = true; };
  }, [open, api, date, test.report, testKey, testPeople]);

  const setS = (k, v) => setSched(s => ({ ...s, [k]: v }));
  const setW = (k, v) => setWa(s => ({ ...s, [k]: v }));
  const setTemplate = (rep, v) => setWa(s => ({ ...s, templates: { ...s.templates, [rep]: v } }));
  const setReportOn = (rep, v) => setWa(s => ({ ...s, reports: { ...s.reports, [rep]: v } }));
  const setMark = (level, key, value) => setMarks(m => ({ ...m, [level]: { ...m[level], [key]: value } }));
  const setItem = (key, field, value) => setRem(r => ({
    ...r, items: r.items.map(it => (it.key === key ? { ...it, [field]: value } : it)) }));

  const save = async () => {
    setSaving(true);
    const newKey = !!wa.api_key.trim();
    try {
      // WhatsApp first: a new key Zoko refuses stops the save before anything is written
      const r = await api.put('/whatsapp/settings', {
        enabled: wa.enabled, api_key: wa.api_key, country_code: wa.country_code,
        public_base_url: wa.public_base_url, link_days: Number(wa.link_days) || 7,
        templates: wa.templates, reports: wa.reports, places: marks,
      });
      setWa({ ...r.data, api_key_masked: r.data.api_key, api_key: '' });
      if (rem) {
        await api.put('/reports/reminders', {
          enabled: rem.enabled,
          items: Object.fromEntries(rem.items.map(it => [it.key, {
            nudge: it.nudge, escalate: it.escalate, last_call: it.last_call,
            days: it.days, owners: it.owners || [], extra: it.extra || '',
          }])),
        });
      }
      await api.put('/reports/schedule', {
        enabled: sched.enabled, timezone: sched.timezone, briefing_time: sched.briefing_time,
        briefing_until: sched.briefing_until, evening_time: sched.evening_time, evening_until: sched.evening_until,
        briefing_build: sched.briefing_build,
        admin_recipients: sched.admin_recipients, email_leaders: sched.email_leaders,
        low_sale: Number(sched.low_sale) || 0,
        beat_performance: !!sched.beat_performance,
        monthly_enabled: !!sched.monthly_enabled,
        monthly_day: Number(sched.monthly_day) || 1,
        monthly_time: sched.monthly_time || '09:00',
        leader_link_days: Number(sched.leader_link_days) || 90,
        leader_link_renew: Number(sched.leader_link_renew) || 0,
        leader_link_alert: Number(sched.leader_link_alert) || 5,
      });
      const sending = sched.enabled ? 'automatic sending is on' : 'automatic sending is off';
      toast.success(newKey ? `Saved — Zoko accepted the new API key; ${sending}` : `Saved — ${sending}`);
      // Read the places back so the counts and switches show what was actually stored
      api.get('/whatsapp/places', { params: { date } }).then(x => { setPlaces(x.data); setMarks(marksFromTree(x.data)); }).catch(() => {});
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const check = async () => {
    setChecking(true);
    try {
      const r = await api.post('/whatsapp/check', {});
      setChecks(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reach Zoko'); }
    finally { setChecking(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/whatsapp/test', { ...test, date });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test message failed'); }
    finally { setTesting(false); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success('Copied')).catch(() => toast.error('Copy failed'));
  };

  const tabs = canEdit
    ? [['schedule', 'Schedule & email'], ['whatsapp', 'WhatsApp'], ['places', 'Where to send'],
       ['links', "Leaders' links"]]
    : [['schedule', 'Schedule & email'], ['whatsapp', 'WhatsApp'], ['links', "Leaders' links"]];
  const sendingNow = places?.summary?.sending ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-auto" data-testid="report-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading pr-8">Report delivery</DialogTitle>
          <DialogDescription>
            When the reports go out, who is told, and how they reach FEs and leaders on WhatsApp.
            {!canEdit && ' You can see the times and send yourself a test; only a Super Admin can change any of it.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-border" role="tablist">
          {tabs.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 text-sm font-heading -mb-px border-b-2 ${tab === k ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`settings-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {!sched || !wa ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2 py-6"><Loader2 size={14} className="animate-spin" /> Loading...</p>
        ) : tab === 'schedule' ? (
          <fieldset disabled={!canEdit} className="contents">
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium">Build and send automatically</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Evening: scorecards and leaders' summaries once the day's sales and visits upload is in.
                  Tomorrow's briefings are built when the beat plan is uploaded, or by the time set below.
                  Morning: the briefings go out.
                </p>
              </div>
              <Switch checked={!!sched.enabled} onCheckedChange={v => setS('enabled', v)} data-testid="schedule-enabled" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Caption>Beat Briefing sent at</Caption>
                <Input type="time" value={sched.briefing_time} onChange={e => setS('briefing_time', e.target.value)} className="h-9" data-testid="briefing-time" />
              </div>
              <div className="space-y-1">
                <Caption>Retry failed sends until</Caption>
                <Input type="time" value={sched.briefing_until} onChange={e => setS('briefing_until', e.target.value)} className="h-9" />
              </div>
              <div className="space-y-1">
                <Caption>Scorecard &amp; leaders at</Caption>
                <Input type="time" value={sched.evening_time} onChange={e => setS('evening_time', e.target.value)} className="h-9" data-testid="evening-time" />
              </div>
              <div className="space-y-1">
                <Caption>Tomorrow's briefings built by</Caption>
                <Input type="time" value={sched.briefing_build || '20:00'}
                  onChange={e => setS('briefing_build', e.target.value)} className="h-9 text-sm"
                  data-testid="briefing-build-time" />
              </div>
              <div className="space-y-1">
                <Caption>Wait for the upload until</Caption>
                <Input type="time" value={sched.evening_until} onChange={e => setS('evening_until', e.target.value)} className="h-9" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              If the day's upload hasn't arrived by {clock12(sched.evening_until)}, no scorecards or summaries go out
              that day — a page of false "No sale" lines would be worse than none — and the people below are told.
            </p>

            <div className="space-y-1">
              <Caption>Call out a day under</Caption>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rs.</span>
                <Input type="number" min={0} step={500} value={sched.low_sale ?? 5000}
                  onChange={e => setS('low_sale', parseInt(e.target.value) || 0)}
                  className="h-9 w-32 text-sm" data-testid="low-sale" />
              </div>
              <p className="text-[11px] text-muted-foreground">
                The leaders' summary lists the FEs who sold less than this today, with what their
                visits looked like. 0 leaves the section out.
              </p>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Beat performance on the leaders' summary</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Every beat each FE works, for the month &mdash; what the beat has been worth, what
                  they have billed and sold there, and which visits happened. Adds about two pages
                  for a typical leader and seven for the largest. Off puts the page back to what it
                  was, from the next run, with nothing already sent touched.
                </p>
              </div>
              <Switch checked={sched.beat_performance !== false}
                onCheckedChange={v => setS('beat_performance', v)} data-testid="beat-performance" />
            </div>

            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <CalendarDays size={14} /> Monthly summary for leaders
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    The month that closed, per manager and per senior leader: each FE against their
                    own last month and their target. Emailed, and sent on WhatsApp where that
                    report is switched on and its template is approved.
                  </p>
                </div>
                <Switch checked={!!sched.monthly_enabled} onCheckedChange={v => setS('monthly_enabled', v)}
                  data-testid="monthly-enabled" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Caption>On this day of the month</Caption>
                  <Input type="number" min={1} max={28} value={sched.monthly_day ?? 1}
                    onChange={e => setS('monthly_day', parseInt(e.target.value) || 1)}
                    className="h-9" data-testid="monthly-day" />
                </div>
                <div className="space-y-1">
                  <Caption>At</Caption>
                  <Input type="time" value={sched.monthly_time || '09:00'}
                    onChange={e => setS('monthly_time', e.target.value)} className="h-9"
                    data-testid="monthly-time" />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Caption>Tell these people how each run went</Caption>
              <Textarea rows={2} placeholder="ops@keyafoods.in, sales.head@keyafoods.in" value={sched.admin_recipients}
                onChange={e => setS('admin_recipients', e.target.value)} className="text-sm" data-testid="admin-recipients" />
              <p className="text-[11px] text-muted-foreground">Each run's summary, with every PDF in a ZIP. Failures are emailed too.</p>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5"><Mail size={14} /> Email leaders their summary</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Each manager gets their Summary for Leaders, and each senior leader their roll-up, at their own
                  address from the User List only - and only where the Where to send tab covers their team.
                </p>
              </div>
              <Switch checked={!!sched.email_leaders} onCheckedChange={v => setS('email_leaders', v)} data-testid="email-leaders" />
            </div>

            {rem && (
              <div className="rounded-md border border-border p-3 space-y-3" data-testid="reminders-section">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium flex items-center gap-1.5"><Clock size={14} /> Chase the uploads the reports live on</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      A nudge to whoever owes us the file while there is still time to act, then once more when
                      the deadline passes. Nothing goes out once the file is in.
                    </p>
                  </div>
                  <Switch checked={!!rem.enabled} onCheckedChange={v => setRem(r => ({ ...r, enabled: v }))}
                    data-testid="reminders-enabled" />
                </div>
                {rem.items.map(it => (
                  <div key={it.key} className="rounded-md bg-muted/30 p-2.5 space-y-2">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <p className="text-xs font-medium">{it.label}
                        <span className="text-muted-foreground font-normal"> — for {it.needs}</span></p>
                      <span className={`text-[11px] ${it.status?.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {it.status?.ok ? 'In' : 'Waiting'}{it.status?.detail ? ` — ${it.status.detail}` : ''}
                      </span>
                    </div>
                    {it.status?.last && (
                      <p className="text-[11px] text-muted-foreground">
                        Last upload {whenLabel(it.status.last.at)} by {it.status.last.by || 'someone'} · {it.status.last.file}
                      </p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="space-y-1"><Caption>Nudge at</Caption>
                        <Input type="time" value={it.nudge || ''} onChange={e => setItem(it.key, 'nudge', e.target.value)}
                          className="h-8 text-xs" data-testid={`nudge-${it.key}`} /></div>
                      <div className="space-y-1"><Caption>Chase again at</Caption>
                        <Input type="time" value={it.escalate || ''} onChange={e => setItem(it.key, 'escalate', e.target.value)}
                          className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Caption>Last call</Caption>
                        <Input type="time" value={it.last_call || ''} onChange={e => setItem(it.key, 'last_call', e.target.value)}
                          className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Caption>On these days</Caption>
                        <DayPicker value={it.days} onChange={v => setItem(it.key, 'days', v)} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Caption>Whose job it is</Caption>
                        <OwnerPicker users={rem.users || []} value={it.owners}
                          onChange={v => setItem(it.key, 'owners', v)} /></div>
                      <div className="space-y-1"><Caption>And this address</Caption>
                        <Input value={it.extra || ''} onChange={e => setItem(it.key, 'extra', e.target.value)}
                          placeholder="ops@keyafoods.in" className="h-8 text-xs" /></div>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  The nudge goes to the people named here; the later ones go to them and to the run summary list
                  above. Nobody in the field is ever written to.
                </p>
              </div>
            )}

            {!sched.smtp_configured && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> Email (SMTP) isn't set up yet — do that on the Configuration page first.
              </p>
            )}

            {(sched.next?.morning || sched.state?.evening || sched.state?.morning) && (
              <div className="rounded-md bg-muted/40 px-3 py-2 space-y-1 text-xs text-muted-foreground" data-testid="schedule-state">
                {sched.next?.morning && <p>Next briefings: <strong className="text-foreground">{whenLabel(sched.next.morning)}</strong> &middot; next evening run: <strong className="text-foreground">{whenLabel(sched.next.evening)}</strong></p>}
                {sched.state?.evening && <p>Evening run {formatDisplayDate(sched.state.evening.date)}: {sched.state.evening.status} {sched.state.evening.detail ? `— ${sched.state.evening.detail}` : ''}</p>}
                {sched.state?.morning && <p>Morning send {formatDisplayDate(sched.state.morning.date)}: {sched.state.morning.status} {sched.state.morning.detail ? `— ${sched.state.morning.detail}` : ''}</p>}
              </div>
            )}
          </div>
          </fieldset>
        ) : tab === 'links' ? (
          <LinksTab api={api} data={links} canEdit={canEdit} onChange={setLinks}
                    sched={sched} setS={setS} />
        ) : tab === 'whatsapp' ? (
          <div className="space-y-4" data-testid="whatsapp-section">
            {!canEdit && (
              <p className="text-xs text-muted-foreground rounded-md border border-border p-3" data-testid="wa-read-only">
                WhatsApp is {wa.enabled ? 'on' : 'off'} for this app. The key, the approved templates
                and which places are switched on are a Super Admin's to change. You can send a test
                below.
              </p>
            )}
            {canEdit && (<>
            <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5"><MessageCircle size={14} /> Send on WhatsApp through Zoko</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Only where it is switched on in the Where to send tab ({sendingNow} FEs as saved), and only the
                  reports switched on below.
                </p>
              </div>
              <Switch checked={!!wa.enabled} onCheckedChange={v => setW('enabled', v)} data-testid="wa-enabled" />
            </div>
            {wa.problem && <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5"><AlertTriangle size={13} /> {wa.problem}</p>}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Caption>Zoko API key</Caption>
                <SecretInput name="zoko-api-key" value={wa.api_key} onChange={e => setW('api_key', e.target.value)}
                  placeholder={wa.has_api_key ? `Saved (${wa.api_key_masked || '••••'}) — leave blank to keep` : 'Paste the API key from Zoko'}
                  className="h-9 text-sm" data-testid="wa-api-key" />
                <p className="text-[11px] text-muted-foreground">Blank keeps the saved key. A new key is checked with Zoko before it is saved.</p>
              </div>
              <div className="space-y-1">
                <Caption>This app's public address</Caption>
                <Input value={wa.public_base_url} onChange={e => setW('public_base_url', e.target.value)}
                  placeholder="https://pulse.keyafoods.in" className="h-9 text-sm" data-testid="wa-base-url" />
                <p className="text-[11px] text-muted-foreground">
                  Zoko fetches each PDF from a private link here.{' '}
                  {!wa.public_base_url && (
                    <button type="button" className="underline" onClick={() => setW('public_base_url', window.location.origin)}>Use {window.location.origin}</button>
                  )}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Caption>Links last (days)</Caption>
                  <Input type="number" min={1} max={30} value={wa.link_days} onChange={e => setW('link_days', e.target.value)} className="h-9 text-sm" />
                </div>
                <div className="space-y-1">
                  <Caption>Country code</Caption>
                  <Input value={wa.country_code} onChange={e => setW('country_code', e.target.value)} className="h-9 text-sm" />
                </div>
              </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-2 font-heading font-medium">Report</th>
                    <th className="text-left px-3 py-2 font-heading font-medium">Approved template</th>
                    <th className="text-center px-3 py-2 font-heading font-medium">Send automatically</th>
                  </tr>
                </thead>
                <tbody>
                  {REPORTS.map(r => (
                    <tr key={r.key} className="border-t border-border/40">
                      <td className="px-3 py-1.5">{r.label}<span className="block text-[10px] text-muted-foreground">{r.who}</span></td>
                      <td className="px-3 py-1.5">
                        <Input value={wa.templates?.[r.key] || ''} onChange={e => setTemplate(r.key, e.target.value)}
                          className="h-8 text-xs" data-testid={`wa-template-${r.key}`} />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Switch checked={!!wa.reports?.[r.key]} onCheckedChange={v => setReportOn(r.key, v)} data-testid={`wa-report-${r.key}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Each template: a document header, then the person's name and the date — as beat_briefing_daily2.
              Switch a report on only once Meta has approved its template.
            </p>

            {wa.webhook_url && (
              <div className="space-y-1">
                <Caption>Delivery updates — add this webhook in Zoko</Caption>
                <div className="flex gap-2">
                  <Input readOnly value={wa.webhook_url} className="h-9 text-xs font-mono" data-testid="wa-webhook-url" />
                  <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => copy(wa.webhook_url)}><Copy size={13} /></Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Event: message delivery update. Keep this address private — it carries the key.</p>
                {wa.webhook_seen?.matched_at ? (
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400" data-testid="webhook-seen">
                    Zoko last reported a delivery {whenLabel(wa.webhook_seen.matched_at)} — the webhook is working.
                  </p>
                ) : wa.webhook_seen?.at ? (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="webhook-seen">
                    Zoko called this address {whenLabel(wa.webhook_seen.at)}, but none of the messages it
                    named match anything we sent. Tell us and we'll look at it.
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="webhook-seen">
                    No delivery update has ever arrived from Zoko. Until this webhook is added there,
                    a message stays on Queued until someone presses Check statuses.
                  </p>
                )}
              </div>
            )}

            </>)}

            <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
              <Button variant="outline" size="sm" className="h-9" onClick={check} disabled={checking || !wa.has_api_key} data-testid="wa-check-btn">
                {checking ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={13} className="mr-1.5" />} Check setup
              </Button>
              <div className="space-y-1">
                <Caption>Test</Caption>
                <Select value={test.report} onValueChange={v => setTest(t => ({ ...t, report: v, key: '' }))}>
                  <SelectTrigger className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{REPORTS.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Caption>Whose PDF</Caption>
                <PersonPicker people={testPeople[testKey] || []} value={test.key}
                  loading={testPeople[testKey] === undefined}
                  onChange={k => setTest(t => ({ ...t, key: k }))} />
              </div>
              <div className="space-y-1 flex-1 min-w-[150px]">
                <Caption>To this number</Caption>
                <Input value={test.phone} onChange={e => setTest(t => ({ ...t, phone: e.target.value }))} placeholder="9876543210" className="h-9 text-sm" data-testid="wa-test-phone" />
              </div>
              <Button variant="outline" size="sm" className="h-9" onClick={sendTest} disabled={testing || !test.phone} data-testid="wa-test-btn">
                {testing ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Send size={13} className="mr-1.5" />} Send test
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">
              {canEdit ? 'Save first — the test uses the saved key.' : 'The test uses the saved key.'} It sends the {formatDisplayDate(date)} PDF
              {testPeople[testKey]?.length === 0
                ? `, and no ${REPORT[test.report].label} has been built for that day yet.`
                : '.'}
            </p>

            {checks && (
              <div className="rounded-md border border-border overflow-hidden" data-testid="wa-check-results">
                {checks.checks.map((c, i) => (
                  <div key={i} className={`flex items-start gap-2 px-3 py-2 text-xs ${i ? 'border-t border-border/40' : ''}`}>
                    {c.ok ? <CheckCircle2 size={13} className="text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                      : <XCircle size={13} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />}
                    <div className="min-w-0"><span className="font-medium">{c.name}</span>
                      <p className="text-muted-foreground mt-0.5 break-words">{c.detail}</p></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <PlacesTab places={places} marks={marks} setMark={setMark} setMarks={setMarks} opened={opened}
            setOpened={setOpened} search={placeSearch} setSearch={setPlaceSearch} sendingNow={sendingNow} />
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
          {canEdit && (
            <Button size="sm" onClick={save} disabled={saving || !sched || !wa} data-testid="save-settings-btn">
              {saving && <Loader2 size={13} className="mr-1.5 animate-spin" />} Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
